import React, { useState } from 'react';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Stack,
  Tooltip,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  Lock as LockIcon,
  MoreVert as MoreVertIcon,
  AutoAwesome,
} from '@mui/icons-material';
import { Slide, PptxFile } from '../../types';
import { isElementLocked, getElementTypeIcon } from '../../utils/slideUtils';
import ProtectedImage from '../common/ProtectedImage';

// Define element type based on PptxFile
type PptxElement = PptxFile['parsedSlides'][number]['elements'][number];

interface SlideElementListProps {
  slide: Slide;
  pptxSlide: PptxFile['parsedSlides'][number]; // Use indexed access type
  slideIndex: number;
  lang: string;
  elementSuggestingStates: Set<string>; // Pass the Set directly
  focusedElementName?: string; // The name of the element that should be highlighted
  hoveredElementIndex?: number | null; // Index of element currently hovered in preview
  onOpenElementMenu: (event: React.MouseEvent<HTMLElement>, slideIndex: number, elementName: string) => void;
  onGenerateElementContent: (
    event: React.MouseEvent<HTMLElement>,
    slideIndex: number,
    elementIndex: number,
    elementName: string,
    elementType: "text" | "picture" | "table" | "chart",
    fromPreview?: boolean,
  ) => void;
  onElementMouseEnter: (elementIndex: number) => void;
  onElementMouseLeave: () => void;
}

const SlideElementList: React.FC<SlideElementListProps> = ({
  slide,
  pptxSlide,
  slideIndex,
  lang,
  elementSuggestingStates,
  focusedElementName,
  hoveredElementIndex,
  onOpenElementMenu,
  onGenerateElementContent,
  onElementMouseEnter,
  onElementMouseLeave,
}) => {
  return (
    <List dense>
      {pptxSlide.elements.map((element: PptxElement, elementIndex: number) => {
        const effectivelyLocked = isElementLocked(element, slide);
        const stateKey = `${slideIndex}-${element.name}`;
        const isFocused = focusedElementName === element.name;
        const isHovered = hoveredElementIndex === elementIndex;
        const isHighlighted = isFocused || isHovered;

        return (
          <ListItem
            key={elementIndex}
            id={`slide-element-${slideIndex}-${elementIndex}`}
            onMouseEnter={() => onElementMouseEnter(elementIndex)}
            onMouseLeave={onElementMouseLeave}
            sx={(theme) => ({ 
              opacity: effectivelyLocked ? 0.7 : 1,
              backgroundColor: isHighlighted ? theme.palette.action.selected : 'transparent',
              borderLeft: `3px ${effectivelyLocked ? 'dotted' : 'solid'} ${isHighlighted ? theme.palette.primary.main : 'transparent'}`,
              paddingLeft: theme.spacing(1),
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: isHighlighted 
                  ? theme.palette.action.selected 
                  : theme.palette.action.hover,
                transition: 'background-color 0.2s ease'
              }
            })}
          >
            <ListItemIcon>
              {effectivelyLocked ? (
                <LockIcon fontSize="small" color="action" />
              ) : (
                getElementTypeIcon(element.type)
              )}
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography
                  variant="caption"
                  color={!slide.formData?.[element.name] && !effectivelyLocked ? 'warning.main' : 'text.secondary'}
                  sx={{
                    fontWeight: !slide.formData?.[element.name] && !effectivelyLocked ? 'bold' : 'normal'
                  }}
                >
                  {element.name}
                </Typography>
              }
              secondary={
                <Stack spacing={1} sx={{overflow: 'hidden'}}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      textDecoration: slide.formData?.[element.name] && !effectivelyLocked ? 'line-through' : 'none'
                    }}
                  >
                    {element.innerText}
                  </Typography>
                  {/* Conditional rendering based on formData and type */}
                  {slide.formData?.[element.name] && !effectivelyLocked && (
                    (element.type === "shape") ? ( // Treat shape as text for display
                      <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {slide.formData[element.name]}
                      </Typography>
                    ) : (element.type === "picture") ? (
                      <Stack sx={{overflow: 'hidden'}}>
                        <Typography variant="body2" color="success.main" noWrap sx={{ textOverflow: 'ellipsis', mb: 1 }}>
                          {typeof slide.formData[element.name] === 'string' ? slide.formData[element.name].split('/').pop() : slide.formData[element.name]}
                        </Typography>
                        <ProtectedImage imageUrl={slide.formData[element.name]} alt={element.name} style={{ maxWidth: '300px', height: 'auto' }} />
                      </Stack>
                    ) : (element.type === "table" || element.type === "chart") ? ( // Combine table/chart
                      (() => {
                        const data = slide.formData[element.name];
                        const isObject = typeof data === 'object' && data !== null;
                        const hasBodyKey = isObject && Object.keys(data).length === 1 && 'body' in data;
                        
                        if (hasBodyKey && Array.isArray(data.body)) {
                          // Render with MUI Table
                          const tableData = data.body;
                          if (tableData.length === 0) {
                            return <Typography variant="body2" color="text.secondary">{lang === "en" ? "Empty table" : "空表格"}</Typography>;
                          }
                          
                          // Assume first row is headers if available
                          const hasHeaders = tableData.length > 0 && tableData[0].values;
                          const headers = hasHeaders ? tableData[0].values : [];
                          const rows = hasHeaders ? tableData.slice(1) : tableData;
                          
                          return (
                            <Table size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
                              {hasHeaders && (
                                <TableHead>
                                   <TableRow>
                                     {headers.map((header: any, index: number) => (
                                       <TableCell 
                                         key={index} 
                                         sx={{ 
                                           py: 0.5, 
                                           px: 1, 
                                           fontSize: '0.68rem',
                                           fontWeight: 'bold',
                                           backgroundColor: 'action.hover'
                                         }}
                                       >
                                         {header}
                                       </TableCell>
                                     ))}
                                   </TableRow>
                                 </TableHead>
                              )}
                                <TableBody>
                                 {rows.map((row: any, rowIndex: number) => (
                                   <TableRow key={rowIndex}>
                                     {(row.values || []).map((cell: any, cellIndex: number) => (
                                       <TableCell 
                                         key={cellIndex} 
                                         sx={{ 
                                           py: 0.5, 
                                           px: 1, 
                                           fontSize: '0.68rem',
                                           wordBreak: 'break-word'
                                         }}
                                       >
                                         {cell}
                                       </TableCell>
                                     ))}
                                   </TableRow>
                                 ))}
                               </TableBody>
                            </Table>
                          );
                        } else {
                          // Fall back to current implementation
                          return (
                            <pre style={{whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '0.8em', margin: 0}}>
                              {isObject ? JSON.stringify(data, null, 2) : String(data)}
                            </pre>
                          );
                        }
                      })()
                    ) : null
                  )}
                </Stack>
              }
            />
            <ListItemSecondaryAction sx={{right: 0}}>
              <Tooltip
                title={effectivelyLocked ? (lang === "en" ? "Element is locked" : "元素已锁定") : elementSuggestingStates.has(stateKey) ? (
                  element.type === "picture" ? (lang === "en" ? "Recommending image..." : "正在推荐图片...") : (lang === "en" ? "Generating..." : "生成中...")
                ) : (
                  element.type === "picture" ? (lang === "en" ? "Recommend image" : "推荐图片") : (lang === "en" ? "Suggest content" : "生成内容")
                )}
              >
                {/* Wrap IconButton to allow tooltip even when disabled */}
                <span>
                  <IconButton
                    onClick={(e) => {
                      // Don't trigger generation if already suggesting
                      if (!elementSuggestingStates.has(stateKey)) {
                        onGenerateElementContent(
                          e,
                          slideIndex,
                          elementIndex,
                          element.name,
                          element.type as "text" | "picture" | "table" | "chart",
                          false
                        );
                      }
                    }}
                    // @ts-ignore
                    color="ai"
                    disabled={
                      effectivelyLocked ||
                      elementSuggestingStates.has(stateKey)
                    }
                    size="small"
                    sx={{
                      width: 34,
                      height: 34,
                      ...(slide.formData?.[element.name] != null && slide.formData?.[element.name] !== ''
                        ? { bgcolor: 'primary.50' }
                        : {}),
                    }}
                  >
                    {elementSuggestingStates.has(stateKey) ? (
                      <CircularProgress size={20} />
                    ) : (
                      <AutoAwesome fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton
                onClick={(e) => onOpenElementMenu(e, slideIndex, element.name)}
                size="small"
                disabled={elementSuggestingStates.has(stateKey)} // Also disable menu when suggesting
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        );
      })}
    </List>
  );
};

export default SlideElementList; 